/**
 * Leitura de Boletim — interpretação da planilha de estoque / vendas / produção.
 */

export interface MapaColunasBoletim {
  produto: number;
  vendas: number;
  estoque: number;
  carteira: number;
  programado: number;
  carga: number;
  atraso: number;
  tProc: number;
  estProcPlanilha: number;
  salCarteiraPlanilha: number;
  salCargaPlanilha: number;
  estSegurPlanilha: number;
}

export interface LinhaBoletimInterpretada {
  /** Índice estável na matriz importada (para marcar fora de linha). */
  indiceMatriz: number;
  produto: string;
  vendas: number;
  estoque: number;
  carteira: number;
  programado: number;
  carga: number;
  atraso: number;
  tProc: number;
  /** Valores lidos da planilha (quando existirem). */
  estProcPlanilha: number | null;
  salCarteiraPlanilha: number | null;
  salCargaPlanilha: number | null;
  estSegurPlanilha: number | null;
  /** Calculados pelo app. */
  estProcCalc: number;
  salCarteiraCalc: number;
  salCargaCalc: number;
  mediaVendasDia: number;
  estSegurCalc: number | null;
}

export interface ResultadoInterpretacaoBoletim {
  indiceCabecalho: number;
  cabecalho: string[];
  mapa: MapaColunasBoletim;
  linhas: LinhaBoletimInterpretada[];
  formulaEstSegur: FormulaEstSegurDescoberta;
  avisos: string[];
}

export interface FormulaEstSegurDescoberta {
  id: string;
  rotulo: string;
  /** Numerador antes de dividir por (Vendas/90). */
  expressao: string;
}

const DIAS_VENDAS = 90;

function normalizarCabecalho(s: string): string {
  return String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/** Converte célula numérica (BR ou US). Aceita negativo: -20, (20), 20-, −20. */
export function parseNumeroPlanilhaBoletim(val: unknown): number | null {
  if (val == null || val === '') return null;
  if (typeof val === 'number' && Number.isFinite(val)) return val;
  if (val instanceof Date) return null;
  let s = String(val).trim();
  if (!s || s === '-' || s === '—') return null;
  s = s.replace(/\s/g, '').replace(/[\u2212\u2013\u2014\uFF0D\uFE63]/g, '-');
  const paren = s.startsWith('(') && s.endsWith(')');
  if (paren) s = s.slice(1, -1);
  let trailingNeg = false;
  if (s.length > 1 && s.endsWith('-') && !s.startsWith('-')) {
    trailingNeg = true;
    s = s.slice(0, -1);
  }
  if (s.includes(',') && s.includes('.')) {
    s = s.replace(/\./g, '').replace(',', '.');
  } else if (s.includes(',')) {
    s = s.replace(',', '.');
  }
  const n = Number(s);
  if (!Number.isFinite(n)) return null;
  if (paren || trailingNeg) return n === 0 ? 0 : -Math.abs(n);
  return n;
}

function indicePorPadrao(cabecalho: string[], teste: (n: string) => boolean): number {
  return cabecalho.findIndex((c) => teste(normalizarCabecalho(c)));
}

function detectarLinhaCabecalho(matriz: string[][]): number {
  const palavrasChave = ['vendas', 'estoq', 'carteira', 'carga', 'programado'];
  let melhor = 0;
  let melhorPts = -1;
  const limite = Math.min(12, matriz.length);
  for (let i = 0; i < limite; i++) {
    const row = matriz[i].map((c) => normalizarCabecalho(c));
    let pts = 0;
    for (const p of palavrasChave) {
      if (row.some((c) => c.includes(p))) pts++;
    }
    if (pts > melhorPts) {
      melhorPts = pts;
      melhor = i;
    }
  }
  return melhorPts >= 2 ? melhor : 0;
}

/** Junta nova importação abaixo da matriz existente (sem repetir cabeçalho). */
export function mesclarMatrizBoletim(existente: string[][], nova: string[][]): string[][] {
  const filtradaNova = nova.filter((row) => row.some((c) => String(c).trim() !== ''));
  if (!existente.length) return filtradaNova;

  const filtradaExist = existente.filter((row) => row.some((c) => String(c).trim() !== ''));
  const idxHeaderNova = detectarLinhaCabecalho(filtradaNova);
  const dadosNovos = filtradaNova.slice(idxHeaderNova + 1);
  return [...filtradaExist, ...dadosNovos];
}

export function contarLinhasDadosMatrizBoletim(matriz: string[][]): number {
  const filtrada = matriz.filter((row) => row.some((c) => String(c).trim() !== ''));
  if (!filtrada.length) return 0;
  const idx = detectarLinhaCabecalho(filtrada);
  return filtrada.slice(idx + 1).filter((row) => row.some((c) => String(c).trim() !== '')).length;
}

export type FiltroSituacaoRelatorioBoletim = 'todos' | SituacaoBoletimProduto;

export function coletarItensRelatorioBoletim(
  relatorio: RelatorioSituacaoBoletim,
  filtro: FiltroSituacaoRelatorioBoletim
): ItemRelatorioBoletim[] {
  if (filtro === 'critico') return relatorio.critico;
  if (filtro === 'atencao') return relatorio.atencao;
  if (filtro === 'ok') return relatorio.ok;
  if (filtro === 'sem_venda') return relatorio.semVenda;
  return [
    ...relatorio.critico,
    ...relatorio.atencao,
    ...relatorio.ok,
    ...relatorio.semVenda,
  ];
}

export function rotuloFiltroSituacaoBoletim(filtro: FiltroSituacaoRelatorioBoletim): string {
  if (filtro === 'critico') return 'Crítico — produzir';
  if (filtro === 'atencao') return 'Atenção — quase zerando';
  if (filtro === 'ok') return 'OK';
  if (filtro === 'sem_venda') return 'Sem venda 90d';
  return 'Todos os produtos';
}

export function mapearColunasBoletim(cabecalho: string[]): MapaColunasBoletim {
  const idx = (fn: (n: string) => boolean) => indicePorPadrao(cabecalho, fn);
  const tem = (i: number) => i >= 0;

  let produto = idx((n) => /^(produto|descricao|item|modelo|nome)/.test(n));
  if (produto < 0) produto = 0;

  const vendas = idx((n) => /^vendas?$/.test(n) || n === 'venda');
  const estoque = idx((n) => /^est(\.|oque)?$/.test(n) || n.startsWith('estoq'));
  const carteira = idx((n) => n.includes('carteira') && !n.includes('sal'));
  const programado = idx((n) => n.includes('programado'));
  const carga = idx(
    (n) => (n === 'carga' || (n.includes('carga') && !n.includes('sal'))) && !n.includes('segur')
  );
  const atraso = idx((n) => n.includes('atraso'));
  const tProc = idx((n) => /t\.?\s*proc/.test(n.replace(/\s/g, '')));
  const estProcPlanilha = idx((n) => /est\+?\s*proc/.test(n.replace(/\s/g, '')));
  const salCarteiraPlanilha = idx((n) => /sal\.?\s*cart/.test(n.replace(/\s/g, '')));
  const salCargaPlanilha = idx((n) => /sal\.?\s*carg/.test(n.replace(/\s/g, '')));
  const estSegurPlanilha = idx((n) => /est\.?\s*segur/.test(n.replace(/\s/g, '')));

  return {
    produto,
    vendas,
    estoque,
    carteira,
    programado,
    carga,
    atraso,
    tProc,
    estProcPlanilha,
    salCarteiraPlanilha,
    salCargaPlanilha,
    estSegurPlanilha,
  };
}

function celulaNum(row: string[], col: number): number | null {
  if (col < 0) return null;
  return parseNumeroPlanilhaBoletim(row[col]);
}

function celulaTexto(row: string[], col: number): string {
  if (col < 0) return '';
  return String(row[col] ?? '').trim();
}

function nz(n: number | null): number {
  return n ?? 0;
}

export const FORMULAS_EST_SEGUR: Array<{
  id: string;
  rotulo: string;
  expressao: string;
  numerador: (r: LinhaBoletimInterpretada) => number;
}> = [
  {
    id: 'estoque',
    rotulo: 'Estoque ÷ (Vendas÷90)',
    expressao: 'Estoq.',
    numerador: (r) => r.estoque,
  },
  {
    id: 'salCarga',
    rotulo: 'Sal.Carga ÷ (Vendas÷90)',
    expressao: 'Est+Proc − Carteira',
    numerador: (r) => r.salCargaCalc,
  },
  {
    id: 'salCarteira',
    rotulo: 'Sal.Carteira ÷ (Vendas÷90)',
    expressao: 'Est+Proc − Carteira',
    numerador: (r) => r.salCarteiraCalc,
  },
  {
    id: 'estProc',
    rotulo: 'Est+Proc ÷ (Vendas÷90)',
    expressao: 'Estoq. + T.Proc.',
    numerador: (r) => r.estProcCalc,
  },
  {
    id: 'estMenosCarteiraCarga',
    rotulo: '(Estoq.−Carteira−Carga) ÷ (Vendas÷90)',
    expressao: 'Estoq. − Carteira − Carga',
    numerador: (r) => r.estoque - r.carteira - r.carga,
  },
  {
    id: 'estMaisProgMenosCartCarga',
    rotulo: '(Estoq.+Programado−Carteira−Carga) ÷ (Vendas÷90)',
    expressao: 'Estoq. + Programado − Carteira − Carga',
    numerador: (r) => r.estoque + r.programado - r.carteira - r.carga,
  },
];

function calcularEstSegurDias(numerador: number, vendas90: number): number | null {
  if (vendas90 <= 0) return null;
  const mediaDia = vendas90 / DIAS_VENDAS;
  if (mediaDia <= 0) return null;
  return numerador / mediaDia;
}

function descobrirFormulaEstSegur(linhas: LinhaBoletimInterpretada[]): FormulaEstSegurDescoberta {
  const comRef = linhas.filter(
    (r) => r.estSegurPlanilha != null && r.vendas > 0 && Number.isFinite(r.estSegurPlanilha)
  );
  if (comRef.length === 0) {
    const padrao = FORMULAS_EST_SEGUR.find((f) => f.id === 'salCarga')!;
    return {
      id: padrao.id,
      rotulo: padrao.rotulo,
      expressao: padrao.expressao,
    };
  }

  let melhor = FORMULAS_EST_SEGUR[0];
  let menorErro = Infinity;
  for (const cand of FORMULAS_EST_SEGUR) {
    let somaErro = 0;
    let n = 0;
    for (const r of comRef) {
      const calc = calcularEstSegurDias(cand.numerador(r), r.vendas);
      if (calc == null) continue;
      somaErro += Math.abs(calc - (r.estSegurPlanilha as number));
      n++;
    }
    if (n > 0 && somaErro / n < menorErro) {
      menorErro = somaErro / n;
      melhor = cand;
    }
  }

  return {
    id: melhor.id,
    rotulo: melhor.rotulo,
    expressao: melhor.expressao,
  };
}

function aplicarFormulaEstSegur(
  linha: LinhaBoletimInterpretada,
  formula: FormulaEstSegurDescoberta
): number | null {
  const cand = FORMULAS_EST_SEGUR.find((f) => f.id === formula.id) ?? FORMULAS_EST_SEGUR[0];
  return calcularEstSegurDias(cand.numerador(linha), linha.vendas);
}

export function interpretarMatrizBoletim(matriz: string[][]): ResultadoInterpretacaoBoletim {
  const avisos: string[] = [];
  const filtrada = matriz.filter((row) => row.some((c) => String(c).trim() !== ''));
  const indiceCabecalho = detectarLinhaCabecalho(filtrada);
  const cabecalho = filtrada[indiceCabecalho]?.map((c) => String(c ?? '')) ?? [];
  const mapa = mapearColunasBoletim(cabecalho);

  if (mapa.vendas < 0) avisos.push('Coluna "Vendas" não encontrada no cabeçalho.');
  if (mapa.estoque < 0) avisos.push('Coluna "Estoq." não encontrada no cabeçalho.');

  const linhasBrutas = filtrada.slice(indiceCabecalho + 1);
  const linhas: LinhaBoletimInterpretada[] = [];

  for (let i = 0; i < linhasBrutas.length; i++) {
    const row = linhasBrutas[i];
    const indiceMatriz = indiceCabecalho + 1 + i;
    const produto = celulaTexto(row, mapa.produto);
    if (!produto) continue;
    const vendas = nz(celulaNum(row, mapa.vendas));
    const estoque = nz(celulaNum(row, mapa.estoque));
    const carteira = nz(celulaNum(row, mapa.carteira));
    const programado = nz(celulaNum(row, mapa.programado));
    const carga = nz(celulaNum(row, mapa.carga));
    const atraso = nz(celulaNum(row, mapa.atraso));
    const tProc = nz(celulaNum(row, mapa.tProc));

    const estProcCalc = estoque + tProc;
    const salCarteiraCalc = estProcCalc - carteira;
    const salCargaCalc = estProcCalc - carteira;
    const mediaVendasDia = vendas > 0 ? vendas / DIAS_VENDAS : 0;

    linhas.push({
      indiceMatriz,
      produto,
      vendas,
      estoque,
      carteira,
      programado,
      carga,
      atraso,
      tProc,
      estProcPlanilha: celulaNum(row, mapa.estProcPlanilha),
      salCarteiraPlanilha: celulaNum(row, mapa.salCarteiraPlanilha),
      salCargaPlanilha: celulaNum(row, mapa.salCargaPlanilha),
      estSegurPlanilha: celulaNum(row, mapa.estSegurPlanilha),
      estProcCalc,
      salCarteiraCalc,
      salCargaCalc,
      mediaVendasDia,
      estSegurCalc: null,
    });
  }

  const formulaEstSegur = descobrirFormulaEstSegur(linhas);
  for (const linha of linhas) {
    linha.estSegurCalc = aplicarFormulaEstSegur(linha, formulaEstSegur);
  }

  return {
    indiceCabecalho,
    cabecalho,
    mapa,
    linhas,
    formulaEstSegur,
    avisos,
  };
}

export function formatarDiasBoletim(dias: number | null | undefined): string {
  if (dias == null || !Number.isFinite(dias)) return '—';
  return dias.toLocaleString('pt-BR', { maximumFractionDigits: 1, minimumFractionDigits: 0 });
}

export function numerosConferem(
  a: number | null | undefined,
  b: number | null | undefined,
  tolerancia = 0.6
): boolean | null {
  if (a == null && b == null) return null;
  if (a == null || b == null) return false;
  return Math.abs(a - b) <= tolerancia;
}

export type SituacaoBoletimProduto = 'critico' | 'atencao' | 'ok' | 'sem_venda';

export interface ItemRelatorioBoletim {
  linha: LinhaBoletimInterpretada;
  situacao: SituacaoBoletimProduto;
  motivos: string[];
  /** Menor = mais urgente (para ordenação). */
  prioridade: number;
}

export interface RelatorioSituacaoBoletim {
  critico: ItemRelatorioBoletim[];
  atencao: ItemRelatorioBoletim[];
  ok: ItemRelatorioBoletim[];
  semVenda: ItemRelatorioBoletim[];
  limiteDiasAtencao: number;
  totais: {
    produtos: number;
    produtosAtivos: number;
    foraDeLinha: number;
    critico: number;
    atencao: number;
    ok: number;
    semVenda: number;
  };
}

export function linhaEstaForaDeLinha(
  linha: LinhaBoletimInterpretada,
  foraDeLinha: ReadonlySet<number>
): boolean {
  return foraDeLinha.has(linha.indiceMatriz);
}

/** Ativos primeiro; fora de linha no final, na ordem original entre si. */
export function ordenarLinhasTabelaBoletim(
  linhas: LinhaBoletimInterpretada[],
  foraDeLinha: ReadonlySet<number>
): LinhaBoletimInterpretada[] {
  const ativos: LinhaBoletimInterpretada[] = [];
  const fora: LinhaBoletimInterpretada[] = [];
  for (const linha of linhas) {
    if (foraDeLinha.has(linha.indiceMatriz)) fora.push(linha);
    else ativos.push(linha);
  }
  return [...ativos, ...fora];
}

export function filtrarLinhasAtivasBoletim(
  linhas: LinhaBoletimInterpretada[],
  foraDeLinha: ReadonlySet<number>
): LinhaBoletimInterpretada[] {
  return linhas.filter((l) => !foraDeLinha.has(l.indiceMatriz));
}

export const LIMITE_DIAS_ATENCAO_PADRAO = 30;

function ehCritico(l: LinhaBoletimInterpretada): string[] {
  const motivos: string[] = [];
  if (l.vendas > 0 && l.estoque <= 0) motivos.push('Estoque zerado');
  if (l.salCargaCalc < 0) motivos.push(`Sal.Carga negativo (${l.salCargaCalc})`);
  if (l.salCarteiraCalc < 0) motivos.push(`Sal.Carteira negativo (${l.salCarteiraCalc})`);
  if (l.estSegurCalc != null && l.estSegurCalc < 0) {
    motivos.push(`Est.Segur. ${formatarDiasBoletim(l.estSegurCalc)} dias`);
  }
  return motivos;
}

function ehAtencao(l: LinhaBoletimInterpretada, limiteDias: number): string[] {
  const motivos: string[] = [];
  if (l.vendas <= 0) return motivos;
  if (l.estSegurCalc != null && l.estSegurCalc >= 0 && l.estSegurCalc < limiteDias) {
    motivos.push(`Cobertura ${formatarDiasBoletim(l.estSegurCalc)} dias (< ${limiteDias})`);
  }
  if (
    l.mediaVendasDia > 0 &&
    l.salCargaCalc >= 0 &&
    l.salCargaCalc < l.mediaVendasDia * 15 &&
    l.estSegurCalc != null &&
    l.estSegurCalc >= 0
  ) {
    motivos.push(`Sal.Carga baixo (${Math.round(l.salCargaCalc)} un)`);
  }
  if (l.atraso > 0 || l.tProc > 0) {
    motivos.push(`Atraso/T.Proc. (${l.atraso + l.tProc} un)`);
  }
  return motivos;
}

function prioridadeItem(l: LinhaBoletimInterpretada, situacao: SituacaoBoletimProduto): number {
  if (situacao === 'critico') {
    const dias = l.estSegurCalc ?? 0;
    return dias * 1000 + l.salCargaCalc;
  }
  if (situacao === 'atencao') {
    return l.estSegurCalc ?? 9999;
  }
  return l.estSegurCalc ?? 99999;
}

export function montarRelatorioSituacaoBoletim(
  linhas: LinhaBoletimInterpretada[],
  limiteDiasAtencao = LIMITE_DIAS_ATENCAO_PADRAO,
  foraDeLinha: ReadonlySet<number> = new Set()
): RelatorioSituacaoBoletim {
  const linhasAtivas = filtrarLinhasAtivasBoletim(linhas, foraDeLinha);
  const critico: ItemRelatorioBoletim[] = [];
  const atencao: ItemRelatorioBoletim[] = [];
  const ok: ItemRelatorioBoletim[] = [];
  const semVenda: ItemRelatorioBoletim[] = [];

  for (const linha of linhasAtivas) {
    if (linha.vendas <= 0) {
      semVenda.push({
        linha,
        situacao: 'sem_venda',
        motivos: ['Sem vendas nos últimos 90 dias'],
        prioridade: 99999,
      });
      continue;
    }

    const motivosCrit = ehCritico(linha);
    if (motivosCrit.length > 0) {
      critico.push({
        linha,
        situacao: 'critico',
        motivos: motivosCrit,
        prioridade: prioridadeItem(linha, 'critico'),
      });
      continue;
    }

    const motivosAtt = ehAtencao(linha, limiteDiasAtencao);
    if (motivosAtt.length > 0) {
      atencao.push({
        linha,
        situacao: 'atencao',
        motivos: motivosAtt,
        prioridade: prioridadeItem(linha, 'atencao'),
      });
      continue;
    }

    ok.push({
      linha,
      situacao: 'ok',
      motivos: ['Cobertura dentro do limite'],
      prioridade: prioridadeItem(linha, 'ok'),
    });
  }

  critico.sort((a, b) => a.prioridade - b.prioridade);
  atencao.sort((a, b) => a.prioridade - b.prioridade);
  ok.sort((a, b) => (a.linha.estSegurCalc ?? 9999) - (b.linha.estSegurCalc ?? 9999));

  return {
    critico,
    atencao,
    ok,
    semVenda,
    limiteDiasAtencao,
    totais: {
      produtos: linhas.length,
      produtosAtivos: linhasAtivas.length,
      foraDeLinha: foraDeLinha.size,
      critico: critico.length,
      atencao: atencao.length,
      ok: ok.length,
      semVenda: semVenda.length,
    },
  };
}
